import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import authService from '../services/authService';
import { getImageUrl, getApiUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ClubDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Booking states
  const [bookingConfig, setBookingConfig] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const isLoggedIn = authService.isAuthenticated();
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    fetchClubDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch booking data when membership status changes
  useEffect(() => {
    if (isLoggedIn && isMember) {
      fetchBookingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember]);

  const fetchClubDetails = async () => {
    try {
      setLoading(true);
      const apiUrl = getApiUrl();
      
      const response = await fetch(`${apiUrl}/clubs/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch club details');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setClub(result.data);
        
        // Check if current user is a member
        if (isLoggedIn && result.data.members && currentUser) {
          const memberExists = result.data.members.some(
            member => member.id === currentUser.id
          );
          setIsMember(memberExists);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching club details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingData = async () => {
    try {
      console.log('Fetching booking data for club:', id);
      const [configResult, bookingsResult] = await Promise.all([
        authService.authenticatedFetch(`${API_URL}/clubs/${id}/booking-config`),
        authService.authenticatedFetch(
          `${API_URL}/clubs/${id}/bookings?startDate=${getMonthStart()}&endDate=${getMonthEnd()}`
        )
      ]);

      console.log('Booking config result:', configResult);
      console.log('Bookings result:', bookingsResult);

      if (configResult.success) {
        setBookingConfig(configResult.data);
        console.log('Booking config set:', configResult.data);
      } else {
        console.log('No booking config available');
      }

      if (bookingsResult.success) {
        setBookings(bookingsResult.data);
      }
    } catch (error) {
      console.error('Error loading booking data:', error);
    }
  };

  const getMonthStart = () => {
    const date = new Date(currentMonth);
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
  };

  const getMonthEnd = () => {
    const date = new Date(currentMonth);
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  const handleJoinClub = async () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    try {
      setIsProcessing(true);
      const apiUrl = getApiUrl();
      
      const token = authService.getAccessToken();
      
      const response = await fetch(`${apiUrl}/clubs/${id}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setIsMember(true);
        fetchClubDetails();
        // Fetch booking data after joining
        fetchBookingData();
      } else {
        alert(result.message || 'Failed to join club');
      }
    } catch (err) {
      console.error('Error joining club:', err);
      alert('Failed to join club');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLeaveClub = async () => {
    if (!window.confirm('Are you sure you want to leave this club?')) {
      return;
    }

    try {
      setIsProcessing(true);
      const apiUrl = getApiUrl();
      
      const token = authService.getAccessToken();
      
      const response = await fetch(`${apiUrl}/clubs/${id}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setIsMember(false);
        fetchClubDetails();
      } else {
        alert(result.message || 'Failed to leave club');
      }
    } catch (err) {
      console.error('Error leaving club:', err);
      alert('Failed to leave club');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const result = await authService.authenticatedFetch(
        `${API_URL}/bookings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clubId: club.id,
            userId: currentUser.id,
            bookingDate: selectedDate.toISOString().split('T')[0],
            startTime: selectedTimeSlot.start,
            endTime: selectedTimeSlot.end,
            notes: bookingNotes
          })
        }
      );

      if (result.success) {
        await fetchBookingData();
        setShowBookingModal(false);
        setSelectedDate(null);
        setSelectedTimeSlot(null);
        setBookingNotes('');
        alert('Booking created successfully!');
      } else {
        alert(result.message || 'Error creating booking');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Error creating booking');
    } finally {
      setIsProcessing(false);
    }
  };

  const generateTimeSlots = () => {
    if (!bookingConfig) return [];
    
    const slots = [];
    const [startHour, startMin] = bookingConfig.startTime.split(':').map(Number);
    const [endHour, endMin] = bookingConfig.endTime.split(':').map(Number);
    
    let currentTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;
    
    while (currentTime < endTime) {
      const nextTime = currentTime + bookingConfig.slotDuration;
      const startH = Math.floor(currentTime / 60).toString().padStart(2, '0');
      const startM = (currentTime % 60).toString().padStart(2, '0');
      const endH = Math.floor(nextTime / 60).toString().padStart(2, '0');
      const endM = (nextTime % 60).toString().padStart(2, '0');
      
      slots.push({
        start: `${startH}:${startM}`,
        end: `${endH}:${endM}`
      });
      
      currentTime = nextTime;
    }
    
    return slots;
  };

  const isSlotBooked = (date, slot) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.some(booking => {
      const bookingDateStr = new Date(booking.bookingDate).toISOString().split('T')[0];
      return bookingDateStr === dateStr &&
        booking.status === 'confirmed' &&
        booking.startTime === slot.start &&
        booking.endTime === slot.end;
    });
  };

  const isWorkingDay = (date) => {
    if (!bookingConfig) return false;
    const dayOfWeek = date.getDay();
    const workingDays = bookingConfig.workingDays.split(',').map(d => parseInt(d));
    return workingDays.includes(dayOfWeek);
  };

  const isHoliday = (date) => {
    if (!bookingConfig) return false;
    const dateStr = date.toISOString().split('T')[0];
    const holidays = bookingConfig.holidays || [];
    return holidays.includes(dateStr);
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (!date || !isWorkingDay(date) || isHoliday(date)) return;
    setSelectedDate(date);
    setShowBookingModal(true);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="mt-4 text-base-content/70">Loading club details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="min-h-screen bg-base-100">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="alert alert-error max-w-md mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error || 'Club not found'}</span>
            </div>
            <button
              onClick={() => navigate('/clubs')}
              className="btn btn-primary"
            >
              Back to Clubs
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const daysInMonth = getDaysInMonth();
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Parse games into an array for tag display
  const gamesList = club.games ? club.games.split(',').map(g => g.trim()) : [];

  // Get day of week from meeting day
  const getDayOfWeek = () => {
    if (!club.meetingDay) return 'Not specified';
    return club.meetingDay;
  };

  // Handle book now button click
  const handleBookNowClick = () => {
    console.log('Book Now clicked. isLoggedIn:', isLoggedIn, 'isMember:', isMember, 'bookingConfig:', bookingConfig);
    
    if (!isLoggedIn) {
      // Redirect to login with return URL
      navigate(`/login?returnUrl=${encodeURIComponent(location.pathname)}`);
      return;
    }
    
    if (isMember && bookingConfig) {
      // Scroll to booking section
      document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
    } else if (isMember && !bookingConfig) {
      // Show message if no booking is available
      alert('Booking is not available for this club yet. Please contact the club admin.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background with gradient overlay */}
        <div className="absolute inset-0">
          {club.image ? (
            <>
              <img
                src={getImageUrl(club.image)}
                alt={club.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/85 to-pink-900/90"></div>
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600"></div>
          )}
          
          {/* Animated background patterns */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {club.name}
            </h1>
            
            {club.location && (
              <div className="flex items-center justify-center text-white/90 text-xl mb-8">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {club.location}
              </div>
            )}

            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              {club.description || 'Join our amazing board game community'}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {!isLoggedIn ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookNowClick}
                  className="btn btn-primary btn-lg"
                >
                  Book Now
                </motion.button>
              ) : isMember ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookNowClick}
                  className="btn btn-primary btn-lg"
                >
                  Book a Slot
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleJoinClub}
                  disabled={isProcessing}
                  className="btn btn-primary btn-lg"
                >
                  {isProcessing ? <span className="loading loading-spinner"></span> : 'Join Club'}
                </motion.button>
              )}
              
              {isLoggedIn && isMember && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLeaveClub}
                  disabled={isProcessing}
                  className="btn btn-outline btn-lg text-white border-white hover:bg-white hover:text-primary hover:border-white"
                >
                  {isProcessing ? <span className="loading loading-spinner"></span> : 'Leave Club'}
                </motion.button>
              )}
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="text-white/70"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="card-body items-center text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
                  <svg className="w-8 h-8 text-primary-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-4xl font-bold">{club.memberCount || 0}</h3>
                <p className="text-base-content/70">Active Members</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="card-body items-center text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-secondary rounded-full mb-4">
                  <svg className="w-8 h-8 text-secondary-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-4xl font-bold">{getDayOfWeek()}</h3>
                <p className="text-base-content/70">Meeting Day</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="card-body items-center text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-accent rounded-full mb-4">
                  <svg className="w-8 h-8 text-accent-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-4xl font-bold">{club.boardGamesCount || gamesList.length}</h3>
                <p className="text-base-content/70">Board Games</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About & Games Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              About <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Our Club</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {club.description || 'Welcome to our board game community where enthusiasts gather to play, compete, and have fun!'}
            </p>
          </motion.div>

          {/* Games Tags */}
          {gamesList.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mb-16"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Featured Games</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {gamesList.map((game, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.05 }}
                    className="badge badge-primary badge-lg gap-2 px-6 py-4"
                  >
                    {game}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Members Section */}
      {club.members && club.members.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Community</span>
              </h2>
              <p className="text-xl text-gray-600">Meet the amazing people who make this club special</p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {club.members.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="card bg-base-100 shadow-lg hover:shadow-2xl transition-all"
                >
                  <div className="card-body items-center text-center p-6">
                    <div className="avatar placeholder mb-4">
                      <div className="bg-primary text-primary-content rounded-full w-20 shadow-lg">
                        <span className="text-2xl">
                          {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <h4 className="card-title text-base">
                      {member.firstName} {member.lastName}
                    </h4>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Booking Section */}
      {isLoggedIn && isMember && bookingConfig && (
        <section id="booking-section" className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Book <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Your Slot</span>
              </h2>
              <p className="text-xl text-gray-600">Select a date and time to reserve your spot</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold">{monthName}</h3>
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={prevMonth}
                    className="btn btn-square btn-ghost"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={nextMonth}
                    className="btn btn-square btn-ghost"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                </div>
              </div>
              
              <div className="grid grid-cols-7 gap-2 mb-4">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-sm font-bold text-gray-700 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-2">
                {daysInMonth.map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }
                  
                  const isWorking = isWorkingDay(date);
                  const isHol = isHoliday(date);
                  const dateStr = date.toISOString().split('T')[0];
                  const dayBookings = bookings.filter(b => 
                    new Date(b.bookingDate).toISOString().split('T')[0] === dateStr &&
                    b.status === 'confirmed'
                  );
                  const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                  
                  return (
                    <motion.button
                      key={date.toISOString()}
                      whileHover={!(!isWorking || isHol || isPast) ? { scale: 1.05 } : {}}
                      whileTap={!(!isWorking || isHol || isPast) ? { scale: 0.95 } : {}}
                      onClick={() => handleDateClick(date)}
                      disabled={!isWorking || isHol || isPast}
                      className={`aspect-square p-2 rounded-lg border-2 transition-all ${
                        !isWorking || isHol || isPast
                          ? 'btn-disabled bg-base-300 border-base-300 opacity-50'
                          : dayBookings.length > 0
                          ? 'btn btn-primary border-primary shadow-lg'
                          : 'btn btn-ghost border-base-300 hover:border-primary'
                      }`}
                    >
                      <div className="text-sm font-bold">{date.getDate()}</div>
                      {dayBookings.length > 0 && (
                        <div className="text-xs mt-1">
                          {dayBookings.length}
                        </div>
                      )}
                      {isHol && (
                        <div className="text-xs mt-1">🎉</div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>
      )}


      {/* Booking Modal */}
      {showBookingModal && selectedDate && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="modal-box max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="mb-6">
              <h3 className="text-2xl font-bold">
                Book Your Slot
              </h3>
              <p className="text-base-content/70 mt-1">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
            
            <form onSubmit={handleBookingSubmit} className="space-y-6">
              <div>
                <label className="label">
                  <span className="label-text text-lg font-semibold">Select Time Slot</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {generateTimeSlots().map((slot, index) => {
                    const isBooked = isSlotBooked(selectedDate, slot);
                    return (
                      <motion.button
                        key={index}
                        type="button"
                        disabled={isBooked}
                        onClick={() => setSelectedTimeSlot(slot)}
                        whileHover={!isBooked ? { scale: 1.02 } : {}}
                        whileTap={!isBooked ? { scale: 0.98 } : {}}
                        className={`btn ${
                          isBooked
                            ? 'btn-disabled'
                            : selectedTimeSlot?.start === slot.start
                            ? 'btn-primary'
                            : 'btn-outline'
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <span>{slot.start} - {slot.end}</span>
                          {isBooked && <span className="text-xs">Already Booked</span>}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text text-lg font-semibold">Notes (Optional)</span>
                </label>
                <textarea
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  rows={4}
                  className="textarea textarea-bordered"
                  placeholder="Any special requirements or preferences..."
                />
              </div>

              <div className="modal-action">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedDate(null);
                    setSelectedTimeSlot(null);
                    setBookingNotes('');
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!selectedTimeSlot || isProcessing}
                  className="btn btn-primary"
                >
                  {isProcessing ? <span className="loading loading-spinner"></span> : 'Confirm Booking'}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      <Footer />
    </div>
  );
}

export default ClubDetails;
